import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { loadGoogleMaps, DARK_MAP_STYLE } from "@/lib/googleMaps";
import { Navigation, RefreshCw, Wifi, WifiOff, MapPin } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "Unknown";
  const secs = Math.round((Date.now() - new Date(iso)) / 1000);
  if (secs < 10) return "Just now";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function popupHtml(driver) {
  return `
    <div style="font-family:system-ui,sans-serif;min-width:160px;padding:2px 2px;color:#111">
      <div style="font-weight:700;font-size:13px;margin-bottom:3px">${driver.driver_name}</div>
      <div style="color:#666;font-size:11px">Updated ${timeAgo(driver.updated_at)}</div>
      ${driver.speed != null ? `<div style="color:#666;font-size:11px">${Math.round((driver.speed || 0) * 3.6)} km/h</div>` : ""}
      ${driver.accuracy != null ? `<div style="color:#888;font-size:10px">±${Math.round(driver.accuracy)}m accuracy</div>` : ""}
      <div style="color:#999;font-size:10px;margin-top:3px">${driver.lat.toFixed(5)}, ${driver.lng.toFixed(5)}</div>
    </div>`;
}

export default function DriverTracking() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});      // driver_id -> google.maps.Marker
  const infoRef = useRef(null);       // shared InfoWindow

  // ── Update or create Google Maps markers ────────────────────────────────────
  const updateMarkers = useCallback((driverList) => {
    const google = window.google;
    const map = mapInstance.current;
    if (!map || !google?.maps) return;

    // Remove markers for drivers no longer active
    const activeIds = new Set(driverList.map((d) => d.driver_id));
    Object.keys(markersRef.current).forEach((id) => {
      if (!activeIds.has(id)) {
        markersRef.current[id].setMap(null);
        delete markersRef.current[id];
      }
    });

    driverList.forEach((driver) => {
      const pos = { lat: driver.lat, lng: driver.lng };
      const html = popupHtml(driver);
      let marker = markersRef.current[driver.driver_id];

      if (marker) {
        marker.setPosition(pos);
        marker.__html = html;
      } else {
        marker = new google.maps.Marker({
          position: pos,
          map,
          title: driver.driver_name,
          label: {
            text: driver.driver_name.charAt(0).toUpperCase(),
            color: "#0a0a0f",
            fontWeight: "700",
            fontSize: "12px",
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 13,
            fillColor: "#f59e0b",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
        });
        marker.__html = html;
        marker.addListener("click", () => {
          infoRef.current.setContent(marker.__html);
          infoRef.current.open(map, marker);
        });
        markersRef.current[driver.driver_id] = marker;
      }
    });
  }, []);

  // ── Fetch locations ─────────────────────────────────────────────────────────
  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get("/drivers/locations");
      setDrivers(data);
      setLastUpdate(new Date());
      updateMarkers(data);
    } catch {
      // Fail silently during auto-refresh
    } finally {
      setLoading(false);
    }
  }, [updateMarkers]);

  // ── Init Google map ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !mapRef.current || mapInstance.current) return;
        mapInstance.current = new google.maps.Map(mapRef.current, {
          center: { lat: -27.4698, lng: 153.0251 }, // Brisbane default
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: DARK_MAP_STYLE,
        });
        infoRef.current = new google.maps.InfoWindow();
        loadLocations();
      })
      .catch((e) => {
        console.warn("[DriverTracking] Google Maps failed to load:", e.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-refresh every 15 seconds ──────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(loadLocations, 15000);
    return () => clearInterval(id);
  }, [loadLocations]);

  // ── Pan to driver when clicked in sidebar ──────────────────────────────────
  const flyToDriver = (driver) => {
    setSelected(driver.driver_id);
    const map = mapInstance.current;
    if (map && driver.lat && driver.lng) {
      map.panTo({ lat: driver.lat, lng: driver.lng });
      map.setZoom(15);
      const marker = markersRef.current[driver.driver_id];
      if (marker && infoRef.current) {
        infoRef.current.setContent(marker.__html || popupHtml(driver));
        infoRef.current.open(map, marker);
      }
    }
  };

  return (
    <div data-testid="driver-tracking-page">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="brand-title text-2xl sm:text-3xl">Live Driver Tracking</h1>
        <button
          onClick={loadLocations}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-opacity hover:opacity-80"
          style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8", border: "1px solid rgba(25,230,200,0.15)" }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      <p className="text-white/50 text-sm mb-4">
        {lastUpdate
          ? `Updated ${lastUpdate.toLocaleTimeString("en-AU")} · auto-refreshes every 15s`
          : "Loading…"}
      </p>

      {/* ── Main layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-start">
        {/* Map panel */}
        <div
          className="rounded-2xl overflow-hidden border border-white/10"
          style={{ height: "66vh", minHeight: 420, position: "relative" }}
        >
          <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
          {loading && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: "rgba(10,10,15,0.7)", pointerEvents: "none" }}
            >
              <span className="text-white/60 text-sm animate-pulse">Loading map…</span>
            </div>
          )}
        </div>

        {/* Driver list sidebar */}
        <div>
          <p className="text-xs text-white/40 uppercase tracking-widest font-semibold mb-3 flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: "#f59e0b" }}
            />
            Active Drivers ({drivers.length})
          </p>

          {drivers.length === 0 && !loading && (
            <div
              className="text-sm py-5 px-4 rounded-2xl border border-white/5 bg-white/[0.02] text-center"
              style={{ color: "rgba(255,255,255,0.4)" }}
            >
              <MapPin size={28} className="mx-auto mb-2 opacity-30" />
              No drivers are sharing location.
              <p className="text-xs mt-1 opacity-60">
                Drivers share GPS when logged in to the Driver Portal.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {drivers.map((driver) => {
              const secs = Math.round((Date.now() - new Date(driver.updated_at)) / 1000);
              const isLive = secs < 60;
              const isMe = selected === driver.driver_id;

              return (
                <button
                  key={driver.driver_id}
                  onClick={() => flyToDriver(driver)}
                  className="w-full text-left px-4 py-3.5 rounded-2xl border transition-all"
                  style={{
                    background: isMe ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.02)",
                    borderColor: isMe ? "rgba(245,158,11,0.35)" : "rgba(255,255,255,0.08)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                      style={{ background: "#f59e0b", color: "#0a0a0f" }}
                    >
                      {driver.driver_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {driver.driver_name}
                      </p>
                      <p
                        className="text-xs flex items-center gap-1"
                        style={{ color: isLive ? "#22c55e" : "#f59e0b" }}
                      >
                        {isLive ? <Wifi size={10} /> : <WifiOff size={10} />}
                        {timeAgo(driver.updated_at)}
                      </p>
                    </div>
                    <Navigation size={14} className="text-white/30 shrink-0" />
                  </div>

                  {(driver.speed != null || driver.accuracy != null) && (
                    <div className="mt-2 flex gap-3 text-xs text-white/40">
                      {driver.speed != null && (
                        <span>{Math.round((driver.speed || 0) * 3.6)} km/h</span>
                      )}
                      {driver.accuracy != null && (
                        <span>±{Math.round(driver.accuracy)}m</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-white/45">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: "#f59e0b" }} />
          Driver (last 10 min)
        </span>
        <span className="flex items-center gap-1.5">
          <Wifi size={10} style={{ color: "#22c55e" }} /> Live (&lt;60s)
        </span>
        <span className="flex items-center gap-1.5">
          <WifiOff size={10} style={{ color: "#f59e0b" }} /> Recently seen
        </span>
      </div>
    </div>
  );
}
